import { useState } from 'react';
import { DeleteButton, Empty, ErrorBanner, Field, Loading, Modal, VendorPicker } from '../../components/ui';
import { api } from '../../lib/api';
import { useResource } from '../../lib/hooks';
import type { CsVendor, CsVendorContact, Vendor } from '../../lib/types';
import { LogoPicker, LogoTile, PortalLink } from './csUi';

/**
 * Vendors — the supplier directory, ported from the CS Toolbox.
 *
 * A vendor company, the five portals it turned out to be worth keeping a link
 * to, and the named people we actually deal with there. Those contacts are our
 * contacts AT the vendor, which is why they are their own table rather than
 * core.Employee rows or client contacts — see migration 038.
 */

/** The five links, in the order the card and the form both use. */
const PORTALS: { key: keyof CsVendor & string; label: string }[] = [
  { key: 'PartnerUrl', label: 'Partner' },
  { key: 'TrainingUrl', label: 'Training' },
  { key: 'BillingUrl', label: 'Billing' },
  { key: 'StatusUrl', label: 'Status' },
  { key: 'RoadmapUrl', label: 'Roadmap' },
];

export function CsVendors({ onChanged }: { onChanged: () => void }) {
  const vendors = useResource<CsVendor[]>('/cs/vendors');
  const [editing, setEditing] = useState<{ vendor: CsVendor | null } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = () => {
    vendors.reload();
    onChanged();
  };

  return (
    <>
      <div className="row-between wrap mb-4">
        <p className="muted small">
          Who we buy from, where to log in, and who to call when it breaks.
        </p>
        <button
          type="button"
          className="btn btn-primary btn-sm"
          onClick={() => setEditing({ vendor: null })}
        >
          Add a vendor
        </button>
      </div>

      {error && <ErrorBanner message={error} />}
      {vendors.error && <ErrorBanner message={vendors.error} />}
      {vendors.loading && <Loading label="Loading vendors" />}

      {!vendors.loading &&
        (vendors.data?.length === 0 ? (
          <Empty title="No vendors yet">
            <p className="muted small">
              Add the first one, or import the CS Toolbox's with{' '}
              <span className="mono">npm run import:cs</span>.
            </p>
          </Empty>
        ) : (
          <div className="grid grid-2">
            {(vendors.data ?? []).map((vendor) => (
              <VendorCard
                key={vendor.CsVendorId}
                vendor={vendor}
                onEdit={() => setEditing({ vendor })}
                onDelete={() =>
                  api
                    .del(`/cs/vendors/${vendor.CsVendorId}`)
                    .then(refresh)
                    .catch((e: Error) => setError(e.message))
                }
              />
            ))}
          </div>
        ))}

      {editing && (
        <VendorEditor
          vendor={editing.vendor}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            refresh();
          }}
        />
      )}
    </>
  );
}

function VendorCard({
  vendor,
  onEdit,
  onDelete,
}: {
  vendor: CsVendor;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const links = PORTALS.filter((p) => vendor[p.key]);

  return (
    <section className="card cs-tool">
      <div className="card-head">
        <LogoTile
          path={vendor.HasLogo ? `/cs/vendors/${vendor.CsVendorId}/logo` : null}
          name={vendor.Company}
        />
        <div className="grow">
          <h3>{vendor.Company}</h3>
          <div className="row wrap">
            {links.length > 0 ? (
              links.map((p) => (
                <PortalLink key={p.key} href={vendor[p.key] as string | null}>
                  {p.label}
                </PortalLink>
              ))
            ) : (
              <span className="xs subtle">No portal links</span>
            )}
          </div>
        </div>
      </div>

      <div className="card-body">
        {vendor.Contacts.length === 0 ? (
          <p className="xs subtle">No named contact.</p>
        ) : (
          vendor.Contacts.map((contact) => (
            <div key={contact.CsVendorContactId} className="mini-row row-between wrap">
              <span className="grow">
                <span className="strong">{contact.FullName}</span>
                {contact.RoleTitle && <span className="xs subtle"> · {contact.RoleTitle}</span>}
              </span>
              <span className="xs muted">
                {contact.Email && (
                  <a href={`mailto:${contact.Email}`} className="cs-portal">
                    {contact.Email}
                  </a>
                )}
                {contact.Phone && <span className="mono"> {contact.Phone}</span>}
              </span>
            </div>
          ))
        )}

        {vendor.Notes.trim() && <p className="small mt-3">{vendor.Notes}</p>}
      </div>

      <div className="card-foot row-between">
        <button type="button" className="btn btn-ghost btn-sm" onClick={onEdit}>
          Edit
        </button>
        <DeleteButton what={vendor.Company} onConfirm={onDelete} />
      </div>
    </section>
  );
}

/** A contact being edited, before it has an id of its own. */
interface DraftContact {
  fullName: string;
  roleTitle: string;
  email: string;
  phone: string;
}

function toDraft(contact: CsVendorContact): DraftContact {
  return {
    fullName: contact.FullName,
    roleTitle: contact.RoleTitle ?? '',
    email: contact.Email ?? '',
    phone: contact.Phone ?? '',
  };
}

const BLANK_CONTACT: DraftContact = { fullName: '', roleTitle: '', email: '', phone: '' };

function VendorEditor({
  vendor,
  onClose,
  onSaved,
}: {
  vendor: CsVendor | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [vendorId, setVendorId] = useState<number | null>(vendor?.VendorId ?? null);
  /* Loaded by the editor rather than the page: the picker is the only thing
     that reads the directory, and a modal opened now and then should not put a
     request on every visit to the launcher. */
  const vendors = useResource<Vendor[]>('/vendors');
  const [urls, setUrls] = useState<Record<string, string>>(() =>
    Object.fromEntries(PORTALS.map((p) => [p.key, (vendor?.[p.key] as string | null) ?? ''])),
  );
  const [notes, setNotes] = useState(vendor?.Notes ?? '');
  const [contacts, setContacts] = useState<DraftContact[]>(
    vendor?.Contacts.length ? vendor.Contacts.map(toDraft) : [{ ...BLANK_CONTACT }],
  );
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const setContact = (index: number, patch: Partial<DraftContact>) =>
    setContacts((current) => current.map((c, i) => (i === index ? { ...c, ...patch } : c)));

  const save = () => {
    if (vendorId === null) {
      setError('Pick the company from the vendor directory.');
      return;
    }
    setSaving(true);
    setError(null);

    const body = {
      vendorId,
      partnerUrl: urls.PartnerUrl ?? '',
      trainingUrl: urls.TrainingUrl ?? '',
      billingUrl: urls.BillingUrl ?? '',
      statusUrl: urls.StatusUrl ?? '',
      roadmapUrl: urls.RoadmapUrl ?? '',
      notes,
      /* Blank rows are dropped server-side too — see normalizeContacts in
         routes/cs.ts — so tabbing through an empty row costs nothing. */
      contacts: contacts.filter((c) => c.fullName.trim()),
    };

    const request = vendor
      ? api.put<CsVendor>(`/cs/vendors/${vendor.CsVendorId}`, body)
      : api.post<CsVendor>('/cs/vendors', body);
    request.then(onSaved).catch((e: Error) => {
      setError(e.message);
      setSaving(false);
    });
  };

  return (
    <Modal
      title={vendor ? vendor.Company : 'Add a vendor'}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" onClick={save} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      {error && <ErrorBanner message={error} />}

      <Field
        label="Company"
        hint="From the shared vendor directory. A company that is not listed is added in Settings first."
      >
        <VendorPicker vendors={vendors.data ?? []} value={vendorId} onChange={setVendorId} />
      </Field>

      <div className="grid grid-2">
        {PORTALS.map((portal) => (
          <Field key={portal.key} label={`${portal.label} portal`}>
            <input
              className="input"
              value={urls[portal.key] ?? ''}
              onChange={(e) => setUrls((current) => ({ ...current, [portal.key]: e.target.value }))}
              placeholder="https://…"
            />
          </Field>
        ))}
      </div>

      <div className="field">
        <label>Contacts</label>
        {contacts.map((contact, index) => (
          <div key={index} className="cs-contact-row">
            <input
              className="input"
              placeholder="Name"
              value={contact.fullName}
              onChange={(e) => setContact(index, { fullName: e.target.value })}
            />
            <input
              className="input"
              placeholder="Role"
              value={contact.roleTitle}
              onChange={(e) => setContact(index, { roleTitle: e.target.value })}
            />
            <input
              className="input"
              placeholder="Email"
              value={contact.email}
              onChange={(e) => setContact(index, { email: e.target.value })}
            />
            <input
              className="input"
              placeholder="Phone"
              value={contact.phone}
              onChange={(e) => setContact(index, { phone: e.target.value })}
            />
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => setContacts((current) => current.filter((_, i) => i !== index))}
              aria-label="Remove this contact"
            >
              ✕
            </button>
          </div>
        ))}
        <button
          type="button"
          className="btn btn-ghost btn-sm mt-2"
          onClick={() => setContacts((current) => [...current, { ...BLANK_CONTACT }])}
        >
          Add a contact
        </button>
      </div>

      <Field label="Notes">
        <textarea className="input" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
      </Field>

      <div className="field">
        <label>Logo</label>
        {vendor ? (
          <div className="row wrap">
            <LogoTile
              path={vendor.HasLogo ? `/cs/vendors/${vendor.CsVendorId}/logo` : null}
              name={vendor.Company}
            />
            <LogoPicker
              onPicked={(file) =>
                api
                  .put(`/cs/vendors/${vendor.CsVendorId}/logo`, file)
                  .then(onSaved)
                  .catch((e: Error) => setError(e.message))
              }
              onError={setError}
              label={vendor.HasLogo ? 'Replace' : 'Upload'}
            />
            {vendor.HasLogo && (
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() =>
                  api
                    .del(`/cs/vendors/${vendor.CsVendorId}/logo`)
                    .then(onSaved)
                    .catch((e: Error) => setError(e.message))
                }
              >
                Remove
              </button>
            )}
          </div>
        ) : (
          <span className="xs subtle">Save the vendor first, then add its logo.</span>
        )}
      </div>
    </Modal>
  );
}
